import axios from 'axios'
import * as Sentry from '@sentry/vue'

const APP_ID = import.meta.env.VITE_PYLON_APP_ID
const IDENTITY_VERIFICATION_ENABLED =
  import.meta.env.VITE_PYLON_IDENTITY_VERIFICATION !== 'false'
let scriptLoaded = false
let chatOpen = false
let unreadMessageCount = 0
const chatStateListeners = new Set()
let chatLifecycleBound = false

function notifyChatState() {
  const state = { isOpen: chatOpen, unreadMessageCount }
  chatStateListeners.forEach((listener) => listener(state))
}

function updateChatOpen(nextValue) {
  chatOpen = nextValue
  notifyChatState()
}

function updateUnreadMessageCount(nextValue) {
  unreadMessageCount = Number.isFinite(nextValue) ? Math.max(0, nextValue) : 0
  notifyChatState()
}

function bindChatLifecycle() {
  if (chatLifecycleBound || typeof window.Pylon !== 'function') return
  chatLifecycleBound = true
  window.Pylon('onShow', () => updateChatOpen(true))
  window.Pylon('onHide', () => updateChatOpen(false))
  window.Pylon('onChangeUnreadMessagesCount', updateUnreadMessageCount)
}

function buildUnverifiedChatSettings(user) {
  const nameParts = [user.first_name, user.last_name].filter(Boolean)
  const email = user.email

  return {
    app_id: APP_ID,
    email,
    name: nameParts.length ? nameParts.join(' ') : email,
  }
}

function loadScript() {
  if (scriptLoaded) return
  scriptLoaded = true
  ;(function () {
    const e = window
    const t = document
    const n = function () {
      n.e(arguments)
    }
    n.q = []
    n.e = function (args) {
      n.q.push(args)
    }
    e.Pylon = n
    const r = function () {
      const s = t.createElement('script')
      s.setAttribute('type', 'text/javascript')
      s.setAttribute('async', 'true')
      s.setAttribute('src', `https://widget.usepylon.com/widget/${APP_ID}`)
      const first = t.getElementsByTagName('script')[0]
      first.parentNode.insertBefore(s, first)
    }
    if (t.readyState === 'complete') {
      r()
    } else if (e.addEventListener) {
      e.addEventListener('load', r, false)
    }
  })()
}

export async function bootPylonWidget(user) {
  if (!APP_ID) {
    // Pylon is deliberately unavailable in some environments (such as
    // sandbox). Treat an absent app ID as the feature being disabled, rather
    // than an application error.
    return
  }

  if (!IDENTITY_VERIFICATION_ENABLED) {
    window.pylon = { chat_settings: buildUnverifiedChatSettings(user) }
    loadScript()
    bindChatLifecycle()
    return
  }

  let identity
  try {
    const res = await axios.get('/api/integrations/pylon/identity-hash/')
    const isJson = (res.headers?.['content-type'] || '').includes(
      'application/json',
    )
    identity = isJson ? res.data : undefined
  } catch (err) {
    Sentry.captureException(err, { tags: { context: 'pylon_identity_hash' } })
    window.pylon = { chat_settings: { app_id: APP_ID } }
    loadScript()
    bindChatLifecycle()
    return
  }

  if (!identity?.email || !identity?.email_hash) {
    Sentry.captureMessage('Pylon identity-hash returned no email/email_hash', {
      level: 'warning',
      tags: { context: 'pylon_identity_hash' },
    })
    window.pylon = { chat_settings: { app_id: APP_ID } }
    loadScript()
    bindChatLifecycle()
    return
  }

  const nameParts = [user.first_name, user.last_name].filter(Boolean)
  window.pylon = {
    chat_settings: {
      app_id: APP_ID,
      email: identity.email,
      name: nameParts.length ? nameParts.join(' ') : identity.email,
      email_hash: identity.email_hash,
    },
  }
  loadScript()
  bindChatLifecycle()
}

export function shutdownPylonWidget() {
  if (typeof window.Pylon === 'function') {
    window.Pylon('hide')
    window.Pylon('hideChatBubble')
  }
  // Clear only this app's cached UI state so it cannot leak into the next
  // session. Pylon remains the source of truth; this does not mark messages read.
  updateChatOpen(false)
  updateUnreadMessageCount(0)
}

export function hidePylonWidget() {
  if (typeof window.Pylon === 'function') window.Pylon('hideChatBubble')
}

export function showPylonWidget() {
  if (typeof window.Pylon === 'function') window.Pylon('showChatBubble')
}

export function togglePylonWidget() {
  if (typeof window.Pylon !== 'function') return
  const nextOpenState = !chatOpen
  window.Pylon(nextOpenState ? 'show' : 'hide')
  // Pylon also emits onShow/onHide. Update immediately so a second click
  // reliably reverses the first even if the widget's animation is still busy.
  updateChatOpen(nextOpenState)
}

export function subscribeToPylonChatState(listener) {
  chatStateListeners.add(listener)
  listener({ isOpen: chatOpen, unreadMessageCount })
  return () => chatStateListeners.delete(listener)
}
